import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AdminContext = createContext();

// api instance created ONCE at module level — never recreated
const api = axios.create({ baseURL: '/api/admin', timeout: 15000 });

// Intercept: always attach latest token from localStorage
api.interceptors.request.use(config => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Module-level setter so interceptor can clear admin state without circular ref
let _setAdmin = null;

// Intercept: on 401, clear token and admin state
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('adminToken');
      if (_setAdmin) _setAdmin(null);
    }
    return Promise.reject(err);
  }
);

export function AdminProvider({ children }) {
  const [admin,   setAdmin]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Wire interceptor to this component's setAdmin
  useEffect(() => { _setAdmin = setAdmin; return () => { _setAdmin = null; }; }, []);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { setLoading(false); return; }
    api.get('/verify')
      .then(r => setAdmin(r.data.admin))
      .catch(() => { localStorage.removeItem('adminToken'); setAdmin(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await api.post('/login', { username, password });
    localStorage.setItem('adminToken', res.data.token);
    setAdmin({ username: res.data.username, role: res.data.role, id: res.data.id });
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    setAdmin(null);
  }, []);

  // Stable context value — only changes when admin changes
  const value = useMemo(() => ({ admin, loading, login, logout, api }), [admin, loading, login, logout]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export const useAdmin = () => useContext(AdminContext);
